import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { Bildspel, Lektion, VisatBlock } from '../types'
import { bildspelStartsida, grannLektion, hittaArbetsomrade, laddaBildspel, laddaLektion } from '../lib/lektionsplanering'
import { kapitelIndex } from '../lib/content'
import { hamtaForladdad } from '../lib/forladdat'
import { useSidTitel } from '../lib/seo'
import BlockKort from '../components/BlockKort'
import SlotKort from '../components/SlotKort'
import Tavelvy from '../components/Tavelvy'
import BildspelVy from '../components/BildspelVy'
import UtskriftPortal, { useUtskrift } from '../components/UtskriftPortal'
import ArbetsbladVy from '../components/ArbetsbladVy'
import FeedbackRuta from '../components/FeedbackRuta'

const ARBETSBLAD_HINT: Record<string, string> = {
  A: 'begrepp & förståelse',
  B: 'tillämpning',
  C: 'fördjupning',
}

/** Differentieringstexten är fri prosa i källdatat (schema: string), men följer
 *  i praktiken alltid en konvention med korta inledande etiketter ("Stöd:",
 *  "Utmaning:", "Vid 60-minuterspass:" …) mellan meningarna. Delar upp den vid
 *  meningsgränser till en punktlista och fetmarkerar etiketten när meningen har
 *  en — annars visas meningen som den är. Ingen hårdkodad etikettlista: nya
 *  formuleringar (andra ordval för samma idé) fångas ändå av mönstret. */
function differentieringPunkter(text: string): { etikett: string | null; text: string }[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((mening) => mening.trim())
    .filter(Boolean)
    .map((mening) => {
      const match = mening.match(/^([A-ZÅÄÖ][^.:]{1,40}):\s*(.*)$/s)
      return match ? { etikett: match[1], text: match[2] } : { etikett: null, text: mening }
    })
}

export default function LektionSida() {
  const { aoId, lektionId } = useParams<{ aoId: string; lektionId: string }>()
  // Förladdad av förrenderingen (SSG); tom i webbläsaren → effekten laddar.
  const forladdadLektion = hamtaForladdad<Lektion>(`lektion:${lektionId}`)
  const [lektion, setLektion] = useState<Lektion | null>(forladdadLektion)
  const [bildspel, setBildspel] = useState<Bildspel | null>(null)
  const [saknas, setSaknas] = useState(false)
  const [visarIntro, setVisarIntro] = useState(false)
  const [visarBildspel, setVisarBildspel] = useState(false)
  const [visarExitTicket, setVisarExitTicket] = useState(false)
  const arbetsbladUtskrift = useUtskrift()
  // Vilket förslag som är valt per slot: 0 = huvudförslaget, 1..N = alternativ.
  const [slotVal, setSlotVal] = useState<number[]>(() => (forladdadLektion?.karna_block ?? []).map(() => 0))
  // Vald arbetsbladsvariant (index i arbetsblad-listan; default = huvudförslaget).
  const [arbetsbladVal, setArbetsbladVal] = useState(() =>
    forladdadLektion ? Math.max(0, forladdadLektion.arbetsblad.findIndex((a) => a.huvudforslag)) : 0,
  )
  useSidTitel(lektion ? `Lektion ${lektion.lektionsnummer}: ${lektion.titel}` : undefined)

  useEffect(() => {
    let aktiv = true
    setLektion(null)
    setBildspel(null)
    setSaknas(false)
    setVisarIntro(false)
    setVisarBildspel(false)
    setVisarExitTicket(false)
    arbetsbladUtskrift.avsluta()
    setSlotVal([])
    if (!lektionId) return
    laddaLektion(lektionId).then((l) => {
      if (!aktiv) return
      if (!l) {
        setSaknas(true)
        return
      }
      setLektion(l)
      setSlotVal(l.karna_block.map(() => 0))
      setArbetsbladVal(Math.max(0, l.arbetsblad.findIndex((a) => a.huvudforslag)))
      // Ladda bildspelet om lektionen (eller något block/alternativ) pekar på ett avsnitt.
      const harBildspel =
        Boolean(l.slide_deck_avsnitt) ||
        l.karna_block.some((b) => b.slide_deck_avsnitt || b.alternativ.some((a) => a.slide_deck_avsnitt))
      if (harBildspel) {
        laddaBildspel(l.arbetsomrade_id).then((b) => {
          if (aktiv) setBildspel(b)
        })
      }
    })
    return () => {
      aktiv = false
    }
  }, [lektionId])

  if (saknas) {
    return (
      <div className="wrap">
        <div className="dokument">
          <h1>Lektionen finns inte</h1>
          <Link className="knapp knapp-primar" to={`/lararhandledning/${aoId}`}>
            Till arbetsområdet
          </Link>
        </div>
      </div>
    )
  }

  if (!lektion) return null

  const ao = hittaArbetsomrade(lektion.arbetsomrade_id)
  const { forra, nasta } = grannLektion(lektion.lektion_id)
  // Det valda blocket per slot (huvudförslag eller ett alternativ) — allt nedan
  // (tid, tavelvyer, bildspel) utgår från det läraren faktiskt valt.
  const valtBlock = (i: number): VisatBlock => {
    const val = slotVal[i] ?? 0
    const block = lektion.karna_block[i]
    return val === 0 ? block : (block.alternativ[val - 1] ?? block)
  }
  const aktivaBlock = lektion.karna_block.map((_, i) => valtBlock(i))
  const totalMinuter = aktivaBlock.reduce((s, b) => s + b.varaktighet_min, 0)
  const aktivtSlideAvsnitt = aktivaBlock.find((b) => b.slide_deck_avsnitt)?.slide_deck_avsnitt ?? lektion.slide_deck_avsnitt
  const valtArbetsblad = lektion.arbetsblad[arbetsbladVal] ?? lektion.arbetsblad[0]
  const kopplade = lektion.kopplade_kapitel
    .map((id) => kapitelIndex.find((k) => k.id === id))
    .filter((k): k is NonNullable<typeof k> => Boolean(k))

  return (
    <div className="wrap">
      <header className="kapitelhuvud">
        <nav className="brodsmula" aria-label="Brödsmulor">
          <Link to="/lararhandledning">Lärarhandledning</Link>
          {ao && <> · <Link to={`/lararhandledning/${ao.id}`}>{ao.titel}</Link></>}
        </nav>
        <div className="lektionshuvud-rad">
          <div>
            <span className="kapitel-nr">Lektion {lektion.lektionsnummer}</span>
            <h1>{lektion.titel}</h1>
          </div>
        </div>

        <div className="ao-metarad">
          {kopplade.map((k) =>
            k.publicerad ? (
              <Link key={k.id} to={`/kapitel/${k.slug}`} className="kapitel-chip">
                Kap {k.id}: {k.titel}
              </Link>
            ) : (
              <span key={k.id} className="kapitel-chip kapitel-chip-inaktiv">
                Kap {k.id}: {k.titel}
              </span>
            ),
          )}
          <span className="kapitel-chip kapitel-chip-tid">{totalMinuter} min + exit ticket</span>
        </div>

        <div className="larandemal">
          <h2>Lärandemål</h2>
          <ul>
            {lektion.larandemal.map((mal) => (
              <li key={mal}>{mal}</li>
            ))}
          </ul>
        </div>

        {/* Kör lektionen härifrån: alla tavellägen ett klick bort —
            lektionsintro, helskärmstexter, bildspel och exit ticket. */}
        <div className="korrad no-print" aria-label="Visa på tavlan">
          <span className="korrad-etikett">Visa på tavlan:</span>
          {bildspel && (
            <a className="knapp knapp-nedladdning knapp-liten" href={`/pptx/bildspel-${lektion.arbetsomrade_id}.pptx`}>
              📊 Ladda ner allt som PowerPoint
            </a>
          )}
          <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setVisarIntro(true)}>
            Lektionsintro
          </button>
          {[...aktivaBlock, ...lektion.flex_block]
            .filter((b) => b.helskarm_text)
            .map((b, i) => (
              <BlockTavelknapp key={`${b.block_id}-${i}`} namn={b.namn} text={b.helskarm_text!} />
            ))}
          {bildspel && (
            <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setVisarBildspel(true)}>
              Bildspel
            </button>
          )}
          <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setVisarExitTicket(true)}>
            Exit ticket
          </button>
        </div>
      </header>

      <section aria-labelledby="intro-rubrik">
        <div className="sektionshuvud-med-status">
          <h2 className="sektionsrubrik" id="intro-rubrik">Lektionsintro</h2>
          <button type="button" className="knapp knapp-sekundar knapp-liten no-print" onClick={() => setVisarIntro(true)}>
            🖥 Visa för klassen
          </button>
        </div>
        <p className="sektionsingress">
          Elevnära start som visas i helskärm när lektionen börjar: vad vi gör i dag, hur vi
          arbetar och varför.
        </p>
        <div className="lektionsintro callout">
          <p>{lektion.lektionsintro}</p>
        </div>
      </section>

      <section aria-labelledby="upplaggning-rubrik">
        <h2 className="sektionsrubrik" id="upplaggning-rubrik">Lektionsupplägg</h2>
        <p className="sektionsingress">
          Kärnan i lektionen, i ordning — {totalMinuter} minuter plus exit ticket (~5 min) som
          strukturerat avslut. Varje moment visar ett huvudförslag; finns alternativ kan du byta
          moment mot ett likvärdigt förslag.
        </p>
        <div className="blocktidslinje">
          {lektion.karna_block.map((block, i) => {
            const aktivt = aktivaBlock[i]
            const slideAvsnitt = aktivt.slide_deck_avsnitt ?? (block.slot === 'input' ? lektion.slide_deck_avsnitt : undefined)
            return (
              <div key={`${block.block_id}-${i}`}>
                <SlotKort
                  block={block}
                  nummer={i + 1}
                  valtIndex={slotVal[i] ?? 0}
                  onValj={(v) => setSlotVal((nu) => nu.map((x, j) => (j === i ? v : x)))}
                />
                {slideAvsnitt && bildspel && (
                  <div className="bildspel-koppling no-print">
                    <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setVisarBildspel(true)}>
                      🖥 Öppna bildspelet
                    </button>
                    <span>
                      Föreläsningsunderlaget öppnas på avsnittet (
                      {bildspel.avsnitt.find((a) => a.startSida === bildspelStartsida(bildspel, slideAvsnitt))?.rubrik ?? 'första bilden'}
                      ).
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      <section className="sektion sektion-instudering" aria-labelledby="exitticket-rubrik">
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">🎫</span>
          <h2 id="exitticket-rubrik">Exit ticket</h2>
          <button
            type="button"
            className="knapp knapp-sekundar knapp-liten no-print sektion-huvud-knapp"
            onClick={() => setVisarExitTicket(true)}
          >
            🖥 Visa på tavlan
          </button>
        </div>
        <p className="sektion-ingress">
          Lektionens strukturerade avslut (~5 minuter): kort avstämning som visar vad eleverna
          tagit med sig innan de går. Visas i helskärm.
        </p>
        <ol className="fragelista">
          {lektion.exit_ticket.fragor.map((fraga) => (
            <li key={fraga}>{fraga}</li>
          ))}
        </ol>
      </section>

      <section className="sektion sektion-ovning" aria-labelledby="arbetsblad-rubrik">
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">📝</span>
          <h2 id="arbetsblad-rubrik">Arbetsblad</h2>
          <button
            type="button"
            className="knapp knapp-sekundar knapp-liten no-print sektion-huvud-knapp"
            onClick={arbetsbladUtskrift.skrivUt}
          >
            🖨 Skriv ut arbetsbladet
          </button>
        </div>
        <p className="sektion-ingress">
          Färdig färdighetsträning att dela ut — förhandsvisningen nedan visar bladet som det
          skrivs ut, med svarsrader och svarsytor. Finns flera varianter kan du byta blad.
        </p>
        {lektion.arbetsblad.length > 1 && (
          <div className="slotvaxlare no-print" role="group" aria-label="Byt arbetsbladsvariant">
            {lektion.arbetsblad.map((ab, i) => (
              <button key={ab.variant} type="button" className={arbetsbladVal === i ? 'aktiv' : ''} onClick={() => setArbetsbladVal(i)}>
                {ab.huvudforslag ? 'Huvudblad' : `Variant ${ab.variant}`}
                {ARBETSBLAD_HINT[ab.variant] && <span className="variant-hint"> · {ARBETSBLAD_HINT[ab.variant]}</span>}
              </button>
            ))}
          </div>
        )}
        <div className="arbetsblad">
          <ArbetsbladVy arbetsblad={valtArbetsblad} />
        </div>
      </section>

      <section className="sektion sektion-quiz" aria-labelledby="flex-rubrik">
        <div className="sektion-huvud">
          <span className="sektion-ikon" aria-hidden="true">➕</span>
          <h2 id="flex-rubrik">Fler aktiviteter</h2>
        </div>
        <p className="sektion-ingress">
          Valfria aktiviteter utöver lektionens moment (cirka 15 minuter vardera) — välj en
          eller flera om schemat tillåter ett längre pass.
        </p>
        <div className="flexrutnat">
          {lektion.flex_block.map((block, i) => (
            <BlockKort key={`${block.block_id}-${i}`} block={block} ersattningsbar_med={block.ersattningsbar_med} />
          ))}
        </div>
      </section>

      {lektion.differentiering_kommentar && (
        <section aria-labelledby="differentiering-rubrik">
          <h2 className="sektionsrubrik" id="differentiering-rubrik">Differentiering</h2>
          <div className="callout">
            <ul className="differentiering-lista">
              {differentieringPunkter(lektion.differentiering_kommentar).map((punkt, i) => (
                <li key={i}>
                  {punkt.etikett && <strong>{punkt.etikett}: </strong>}
                  {punkt.text}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <FeedbackRuta typ="lektion" objektId={lektion.lektion_id} benamning="den här lektionen" />

      {visarIntro && (
        <Tavelvy rubrik={lektion.titel} onStang={() => setVisarIntro(false)}>
          <div className="tavelvy-text">{lektion.lektionsintro}</div>
        </Tavelvy>
      )}

      {visarExitTicket && (
        <Tavelvy rubrik="Exit ticket" onStang={() => setVisarExitTicket(false)}>
          <ol className="tavelvy-fragelista">
            {lektion.exit_ticket.fragor.map((fraga) => (
              <li key={fraga}>{fraga}</li>
            ))}
          </ol>
        </Tavelvy>
      )}

      {visarBildspel && bildspel && (
        <BildspelVy
          bildspel={bildspel}
          startSida={bildspelStartsida(bildspel, aktivtSlideAvsnitt)}
          onStang={() => setVisarBildspel(false)}
        />
      )}

      {arbetsbladUtskrift.aktiv && (
        <UtskriftPortal onKlar={arbetsbladUtskrift.avsluta}>
          <ArbetsbladVy arbetsblad={valtArbetsblad} medNamnrad />
        </UtskriftPortal>
      )}

      <nav className="kapitelnav no-print" aria-label="Lektionsnavigering">
        {forra && (
          <Link to={`/lararhandledning/${lektion.arbetsomrade_id}/lektion/${forra.lektion_id}`}>
            <span>← Föregående lektion</span>
            <strong>
              {forra.lektionsnummer}. {forra.titel}
            </strong>
          </Link>
        )}
        {nasta && (
          <Link to={`/lararhandledning/${lektion.arbetsomrade_id}/lektion/${nasta.lektion_id}`} className="nasta">
            <span>Nästa lektion →</span>
            <strong>
              {nasta.lektionsnummer}. {nasta.titel}
            </strong>
          </Link>
        )}
      </nav>
    </div>
  )
}

/** Egen knapp+tavelvy per block i "kör lektionen"-raden, så att varje
 *  helskärmstext kan öppnas med ett klick direkt från sidhuvudet. */
function BlockTavelknapp({ namn, text }: { namn: string; text: string }) {
  const [oppen, setOppen] = useState(false)
  return (
    <>
      <button type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => setOppen(true)}>
        {namn}
      </button>
      {oppen && (
        <Tavelvy rubrik={namn} onStang={() => setOppen(false)}>
          <div className="tavelvy-text">{text}</div>
        </Tavelvy>
      )}
    </>
  )
}
